/**
 * Aptidões exigidas por cada GSE de um PCMSO já aprovado.
 *
 * Existe porque a tela de revisão só vale ANTES da aprovação: depois, o
 * documento que alimenta os ASOs ficava congelado e uma aptidão faltante (o
 * caso da eletricidade no GSE dos eletricistas da PRINER) não tinha conserto
 * pelo app.
 *
 * Marcar/desmarcar grava na hora e registra `origem='manual'` — é essa marca
 * que permite avisar, na próxima revisão do PCMSO, o que foi incluído à mão.
 */
import { useEffect, useState } from "react";

import {
  useAptidoes,
  useAptidoesHerdaveis,
  useDefinirAptidoesDoGhe,
  useGhesDoDocumento,
} from "@/hooks/useApi";
import type { DocumentoPrograma } from "@/types/extracao";
import { rotuloEscopo } from "@/types/extracao";

interface Props {
  documento: DocumentoPrograma;
  onClose: () => void;
}

export function GhesAptidoesModal({ documento, onClose }: Props) {
  const { data: ghes, isLoading } = useGhesDoDocumento(documento.id);
  const { data: catalogo } = useAptidoes();
  const { data: herdaveis } = useAptidoesHerdaveis(documento.id);
  const definir = useDefinirAptidoesDoGhe(documento.id);
  const [salvandoGhe, setSalvandoGhe] = useState<number | null>(null);

  const editavel = documento.status === "aprovado" ||
    documento.status === "rascunho" || documento.status === "em_revisao";

  useEffect(() => {
    if (!definir.isPending) setSalvandoGhe(null);
  }, [definir.isPending]);

  async function alternar(gheId: number, atuais: string[], chave: string) {
    const chaves = atuais.includes(chave)
      ? atuais.filter((c) => c !== chave)
      : [...atuais, chave];
    setSalvandoGhe(gheId);
    try {
      await definir.mutateAsync({ gheId, chaves });
    } catch { /* erro abaixo */ }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 p-4"
      onClick={onClose}>
      <div className="card my-8 w-full max-w-3xl space-y-4 p-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-display text-lg font-semibold">Aptidões por GSE</h3>
            <p className="text-xs text-ink-muted">
              {documento.tipo} · {rotuloEscopo(documento.escopo)}
            </p>
          </div>
          <button className="text-ink-muted hover:text-ink" onClick={onClose}>✕</button>
        </div>

        {!editavel ? (
          <div className="rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-ink-muted">
            Documento <strong>{documento.status}</strong> é histórico e não aceita alteração.
          </div>
        ) : null}

        {herdaveis && herdaveis.length > 0 ? (
          <div className="space-y-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2">
            <p className="text-sm text-amber-900">
              <strong>Atenção:</strong> na versão anterior deste PCMSO havia aptidão
              incluída à mão que não veio nesta revisão. Confira se ainda se aplica —
              o documento novo pode ter tirado de propósito.
            </p>
            {herdaveis.map((h) => (
              <div key={`${h.ghe_id}-${h.chave}`} className="flex items-center gap-2 text-sm">
                <span className="text-amber-900">
                  <strong>{h.codigo}</strong>: {h.nome}
                </span>
                {editavel ? (
                  <button
                    className="btn-secondary px-2 py-0.5 text-xs"
                    disabled={definir.isPending}
                    onClick={() => definir.mutate({
                      gheId: h.ghe_id, chaves: [...h.chaves_atuais, h.chave],
                    })}
                  >
                    aplicar
                  </button>
                ) : null}
              </div>
            ))}
          </div>
        ) : null}

        {isLoading ? (
          <p className="text-ink-muted">Carregando…</p>
        ) : (ghes ?? []).length === 0 ? (
          <p className="text-sm text-ink-muted">
            Este documento ainda não tem GSEs — eles são criados na aprovação.
          </p>
        ) : (
          <div className="space-y-3">
            {(ghes ?? []).map((g) => {
              const atuais = g.aptidoes.map((a) => a.chave);
              return (
                <div key={g.id} className="rounded-md border border-gray-200 p-3">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <span className="font-medium">{g.codigo ?? `GSE #${g.id}`}</span>
                    {g.atendimentos_abertos > 0 ? (
                      <span className="badge bg-amber-100 text-amber-800">
                        {g.atendimentos_abertos} atendimento(s) em aberto
                      </span>
                    ) : null}
                  </div>
                  {g.funcoes.length > 0 ? (
                    <p className="mt-0.5 text-xs text-ink-muted">
                      {g.funcoes.slice(0, 6).join("; ")}
                      {g.funcoes.length > 6 ? ` +${g.funcoes.length - 6}` : ""}
                    </p>
                  ) : null}

                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    {(catalogo ?? []).map((a) => {
                      const ativa = atuais.includes(a.chave);
                      const manual = g.aptidoes.find(
                        (x) => x.chave === a.chave)?.origem === "manual";
                      return (
                        <button
                          key={a.chave}
                          type="button"
                          disabled={!editavel || salvandoGhe === g.id}
                          title={manual ? "incluída à mão" : undefined}
                          className={`badge border transition ${
                            ativa
                              ? "border-anarosa bg-anarosa-50 text-anarosa-700"
                              : "border-gray-200 bg-white text-ink-subtle"
                          }`}
                          onClick={() => alternar(g.id, atuais, a.chave)}
                        >
                          {ativa ? "✓ " : ""}{a.nome}{ativa && manual ? " ✎" : ""}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <p className="text-xs text-ink-muted">
          ✎ = incluída à mão depois da aprovação. Vale para os ASOs emitidos daqui em
          diante; os já emitidos não mudam.
        </p>
        {definir.error ? (
          <p className="text-sm text-anarosa">{(definir.error as Error).message}</p>
        ) : null}
      </div>
    </div>
  );
}
